/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {v4 as uuidv4} from 'uuid';

import {BasketState, ProductItem} from 'coreStore/data/basket/types';
import {ProductItem as OrderProductItem} from 'coreStore/data/orders/types';
import {IMoveProductFromBasketToWishlist} from 'coreStore/data/ui/types';
import ProductModel from 'coreClasses/product/ProductModel';
import {EEventType} from 'coreClasses/events/IEvents';
import ProductItemModel from 'coreClasses/basket/ProductItemModel';
import GiftCertificateItemModel from 'coreClasses/basket/GiftCertificateItemModel';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {useWishlist, useURLs} from 'brandHooks';
import {useProducts, useBannedProducts, useCustomerMgr} from 'coreHooks';
import {useCategoryActions} from 'coreHooks/categories';
import {DATALAYER_CONFIGS} from 'brandConstants';

import {useCommerceAPI, useSfccAPI} from '../contexts';
import {
	selectBasket,
	receiveBasket,
	receiveApplicablePaymentMethods,
	receiveInitializationId,
	receiveBasketId,
} from '../../store/data/basket';
import {isError} from '../utils';
import {useDeferredAction} from 'coreHooks';

interface CartEventProductData {
	product?: ProductModel | unknown;
	variationGroupIdForCartEvents?: boolean;
	productCategoriesIds?: (string | undefined)[];
	eventSourceCategoryId?: string;
	eventSourceCategoryName?: string;
	attributes?: Record<string, string | number>;
}

interface CartEventDataLayer {
	eventSourceCategoryId?: string;
	eventSourceCategoryName?: string;
	productItem?: Record<string, unknown>;
	productData: CartEventProductData;
	source?: string;
}

/** Add to basket errors fixed by getting (or creating) the basket again: OCAPI names and SCAPI types. */
const BASKET_RECOVERABLE_ERRORS = [
	'InvalidCustomerException',
	'BasketNotFoundException',
	'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/invalid-customer',
	'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/basket-not-found',
];

/**
 * useBasket custom hook
 */
export default function useBasket() {
	const {managerRepository} = useGeneralProvider();
	const dispatch = useDispatch();
	const api = useCommerceAPI();
	const sfccAPI = useSfccAPI();
	const CustomerMgr = useCustomerMgr();
	const customer = CustomerMgr.getCurrentCustomer();
	const wishlist = useWishlist();
	const urls = useURLs();
	const basket = useSelector((state) => selectBasket(state)) || null;
	const isContextAvailable = CustomerMgr.isContextAvailable();
	const categoryActions = useCategoryActions();
	const {deferAction} = useDeferredAction(
		[basket, isContextAvailable],
		(basket: BasketState, isContextAvailable: boolean) => Boolean(basket && basket.basketId && isContextAvailable)
	);

	/**
	 * Excludes hidden products from the given array of product items.
	 *
	 * @param {ProductItem[]} orders - The array of product items to exclude hidden products from.
	 *
	 * @returns {ProductItem[]} - The array of product items with hidden products excluded, and with the price of bundle kit products updated to include prices of any hidden products.
	 */
	const filterProducts = (basketData) => {
		const {bundleKitProductsMap, hiddenProductsMap, productItemsExcludingHidden} = sortProducts(basketData);
		let updatedBasketData = basketData;

		if (hiddenProductsMap.size > 0) {
			updatedBasketData.productItems = productItemsExcludingHidden;
			updatedBasketData.productItemsIncludingHidden = basketData?.productItems;

			if (bundleKitProductsMap.size > 0) {
				bundleKitProductsMap.forEach((bundleKitProduct) => {
					calculateBundleKitPrice(bundleKitProduct, hiddenProductsMap);
				});
			}
		}

		return updatedBasketData;
	};

	/**
	 * Sorts the order products to separate bundle kit products, hidden products, and products without hidden products.
	 *
	 * @param {any} entryData - The data containing product items to filter.
	 * @returns {object} An object containing maps of bundle kit products, hidden products, and products without hidden products.
	 */
	const sortProducts = (entryData) => {
		const bundleKitProductsMap: Map<string, ProductItem> = new Map();
		const hiddenProductsMap: Map<string, ProductItem> = new Map();
		const productItemsExcludingHidden: ProductItem[] = [];

		entryData?.productItems?.forEach((productItem) => {
			const {itemId} = productItem;
			const productItemModel = new ProductItemModel(managerRepository, productItem, entryData.currency);
			const isParentBundleKit = productItemModel.isParentBundleKit();
			const isComponentBundleKit = productItemModel.isComponentBundleKit();

			if (isParentBundleKit) {
				bundleKitProductsMap.set(itemId, productItem);
				productItemsExcludingHidden.push(productItem);
			} else if (isComponentBundleKit) {
				hiddenProductsMap.set(productItemModel.getItemId(), productItem);
			} else {
				productItemsExcludingHidden.push(productItem);
			}
		});

		return {bundleKitProductsMap, hiddenProductsMap, productItemsExcludingHidden};
	};

	const calculateBundleKitPrice = (bundleKitProduct: ProductItem, hiddenProductsMap: Map<string, ProductItem>) => {
		const productItemModel = new ProductItemModel(
			managerRepository,
			bundleKitProduct as unknown as OrderProductItem,
			basket.currency,
		);

		bundleKitProduct.price = productItemModel.getPrice();
		bundleKitProduct.priceAfterItemDiscount = productItemModel.getPriceAfterItemDiscount();

		hiddenProductsMap?.forEach(hiddenProduct => {
			if (hiddenProduct?.c_bundleKitId === bundleKitProduct?.itemId) {
				const hiddenProductModel = new ProductItemModel(
					managerRepository,
					hiddenProduct as unknown as OrderProductItem,
					(hiddenProduct as any).currency,
				);

				bundleKitProduct.price += hiddenProductModel.getPrice();
				bundleKitProduct.priceAfterItemDiscount += hiddenProductModel.getPriceAfterItemDiscount();
			}

		});
	};

	const setBasket = (basketData) => {
		dispatch(receiveBasket(filterProducts(basketData)));
	};

	const setApplicablePaymentMethods = (paymentMethods) => {
		dispatch(receiveApplicablePaymentMethods(paymentMethods));
	};

	const setInitializationId = (initializationId) => {
		dispatch(receiveInitializationId(initializationId));
	};

	const getBasketProductIds = useCallback((): string[] => {
		const basketProductsIds: Set<string> = new Set();

		basket?.productItems?.forEach((item: ProductItem) => {
			const variationGroupId: string | undefined = item.c_variationGroupId;
			const variationId: string | undefined = item.productId;

			if (variationGroupId) {
				basketProductsIds.add(variationGroupId);
			}

			if (variationId) {
				basketProductsIds.add(variationId);
			}
		});

		return Array.from(basketProductsIds);
	}, [basket?.productItems]);

	const productIdsInBasket = getBasketProductIds();
	const productsInBasket = useProducts(productIdsInBasket);
	const {banned: bannedProductsInBasket} = useBannedProducts(productsInBasket);

	const self = useMemo(() => {
		return {
			...basket,

			// Check if a this represents a valid basket
			get loaded() {
				return basket && basket.basketId;
			},

			get productItemsCount() {
				return this.productItems.length
					? this.productItems.reduce((sum, productItem) => sum + productItem.quantity, 0)
					: 0;
			},

			get productItems() {
				return (
					basket?.productItems?.map(
						(productItem) => new ProductItemModel(managerRepository, productItem, basket.currency),
					) || []
				);
			},

			get giftCertificateItems() {
				return basket?.giftCertificateItems?.map((item) => new GiftCertificateItemModel(item)) || [];
			},

			get giftCertificateItemsCount() {
				return basket?.giftCertificateItems?.length || 0;
			},

			get itemsCount() {
				return this.productItemsCount + this.giftCertificateItemsCount;
			},

			get hasItems() {
				return this.itemsCount > 0;
			},

			get subTotal() {
				const giftCertificateItemsCount = this.giftCertificateItemsCount;
				let subTotal = basket.productSubTotal || 0;

				if (giftCertificateItemsCount) {
					this?.giftCertificateItems?.forEach((giftCertificate) => {
						subTotal += giftCertificate.getPrice();
					});
				}

				return subTotal;
			},

			get checkoutUrl() {
				return urls.CART;
			},

			/** Items taking into account quantity of each */
			get itemAccumulatedCount() {
				return basket?.productItems?.reduce((prev, next) => prev + next.quantity, 0) || 0;
			},

			/** Sum of all order-level discounts */
			get totalOrderDiscount() {
				return (
					basket?.orderPriceAdjustments?.reduce((sum, item) => {
						return sum + item.price;
					}, 0) || 0
				);
			},

			/** Sum of all product-level discounts for the basket */
			get totalProductsDiscounts() {
				return (
					basket?.productItems?.reduce((sum, productItem) => {
						const productAdjustment = productItem.priceAdjustments?.reduce((adjustmentsSum, priceAdjustment) => {
							return adjustmentsSum + priceAdjustment.price;
						}, 0) || 0;

						return sum + productAdjustment;
					}, 0) || 0
				);
			},

			/** Calculates the basket total before any product discounts are applied */
			get totalBeforeDiscount() {
				return this.subTotal + Math.abs(this.totalProductsDiscounts);
			},

			get shippingAddress() {
				return (
					this.loaded && basket?.shipments && basket?.shipments.length && basket?.shipments[0].shippingAddress
				);
			},

			/**
			 * Invalidates the current basket
			 */
			invalidate(): void {
				dispatch(receiveBasketId(null));
			},

			/**
			 * Checks if the basket is ready to use
			 * @returns {boolean} is ready to use
			 */
			isReady(): boolean {
				return this.loaded;
			},

			/**
			 * Fetch products in the basket
			 * @returns {Promise<ProductModel[]>} get products promise
			 */
			async fetchProductsInBasket(): Promise<ProductModel[]> {
				return await managerRepository.getProductsMgr().getProducts(getBasketProductIds()) as ProductModel[];
			},

			/**
			 * Retrieves unique product IDs from the provided basket's product items.
			 * @returns {String[]} An array of unique product IDs derived from the basket's product items.
			 */
			getBasketProductIds() {
				return getBasketProductIds();
			},

			/**
			 * Returns true if basket has preorder product
			 * @returns {boolean}
			 */
			hasPreorder() {
				return basket?.productItems?.some((item) => {
					const product = managerRepository.getProductsMgr().getProduct(item.productId) as any;

					return product.isPreorder() && product?.inventory?.stockLevel < item.quantity;
				});
			},

			/**
			 * Returns true if basket has banned product (based on customer groups)
			 * @returns {boolean}
			 */
			hasBannedProducts() {
				return (bannedProductsInBasket?.length || 0) > 0;
			},

			/**
			 * Returns gift certificate item model
			 * @param {string} gid - gift certificate line item id
			 * @returns {GiftCertificateItemModel|null}
			 */
			getGiftCertificateItem(gid: string) {
				const giftItem = basket?.giftCertificateItems?.find((item) => item.giftCertificateItemId === gid);

				return giftItem ? new GiftCertificateItemModel(giftItem) : null;
			},

			/** Sum of all discounts */
			get totalDiscount() {
				return this.totalProductsDiscounts + this.totalOrderDiscount;
			},

			/** Returns an array of all promotion IDs currently applied to the basket */
			get promotionIds() {
				return basket?.couponItems?.map(item => item.couponItemId) || [];
			},

			/** Returns an array of all promotion names currently applied to the basket */
			get promotionNames() {
				return basket?.couponItems?.map(item => item.code) || [];
			},

			get selectedShippingMethod() {
				return basket?.shipments?.length ? basket.shipments[0].shippingMethod : null;
			},

			/**
			 * Get an existing basket, if basket doesn't exist, create a new one.
			 *
			 * NOTE: This request is the only time we are using the ShopperCustomers API
			 * to interact with a customer basket. All other calls are done through the
			 * ShopperBaskets API, which in our case, uses OCAPI rather than commerce sdk.
			 */
			async getOrCreateBasket() {
				let customerBaskets;
				let basket;

				customerBaskets = await api.shopperCustomers.getCustomerBaskets({
					/**
					 * We take fresh customer from the Mgr because it could be changed
					 * and the hook may be not yet updated.
					 */
					parameters: {customerId: CustomerMgr.getCurrentCustomer()?.customerId},
				});

				// Throw if there was a problem getting the customer baskets
				if (isError(customerBaskets)) {
					switch (customerBaskets.type) {
						case 'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/invalid-customer':
							CustomerMgr.resetStore();
							basket = await CustomerMgr.loginCustomer().then(() => this.getOrCreateBasket());

							break;
						default:
							throw new Error(customerBaskets);
					}
				} else {
					basket = Array.isArray(customerBaskets?.baskets) && customerBaskets.baskets.find(basket => !basket.temporaryBasket);
				}

				if (!basket) {
					// Back to using ShopperBaskets for all basket interaction.
					basket = await api.shopperBaskets.createBasket({});

					await this.initSfccBasket(basket.basketId);

					// Throw if there was a problem creating the basket
					if (isError(basket)) {
						switch (basket.type) {
							case 'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/customer-baskets-quota-exceeded':
								await api.shopperBaskets.deleteBasket({parameters: {
									basketId: basket.basketIds
								}});

								basket = await this.getOrCreateBasket();

								break;
							default:
								throw new Error(basket);
						}
					}
				}

				const currency = managerRepository.getCommerceApi().getConfig().currency;

				// Update basket currency if it was created with the wrong one, this will also set the state.
				if (currency && basket.currency !== currency) {
					await this.updateBasketCurrency(currency, basket.basketId);
				} else {
					setBasket(basket);
				}

				return basket;
			},

			/**
			 * Update the currency of the basket
			 * @param currency - The currency code.
			 * @param basketId - The id of the basket.
			 * @returns {Promise<void>}
			 */
			async updateBasketCurrency(currency, basketId) {
				const updateBasket = await api.shopperBaskets.updateBasket({
					body: {currency},
					parameters: {basketId},
				});

				if (isError(updateBasket)) {
					throw new Error(updateBasket);
				} else {
					setBasket(updateBasket);
				}
			},

			/**
			 * Add an item to the basket.
			 *
			 * @param {array} item
			 * @param {string} item.productId - The id of the product.
			 * @param {number} item.quantity - The quantity of the item.
			 */
			async addItemToBasket(item, dataLayer?) {
				return deferAction(async (basket: BasketState) => {
					const addToBasket = (basketId: string) =>
						api.shopperBaskets.addItemToBasket({body: item, parameters: {basketId}});
					let response;

					try {
						response = await addToBasket(basket.basketId);
					} catch (error) {
						if (!BASKET_RECOVERABLE_ERRORS.includes(error.message.type)) {
							throw error;
						}

						response = await this.getOrCreateBasket().then((fixedBasket) => addToBasket(fixedBasket.basketId));
					}

					// SCAPI errors resolve (`didReceiveResponse` marks them `isError`), they do not throw.
					if (response.isError && BASKET_RECOVERABLE_ERRORS.includes(response.type)) {
						response = await this.getOrCreateBasket().then((fixedBasket) => addToBasket(fixedBasket.basketId));
					}

					// The basket in the store must not become the error (count 0, basket id lost).
					if (response.fault || response.isError) {
						throw new Error(response.message || response.detail);
					}

					setBasket(response);

					const addedItem = item[0];
					const basketItem = response.productItems.find(
						(i) => i.productId === addedItem.productId || i?.c_originProductId === addedItem.productId,
					);

					if (basketItem) {
						const product = managerRepository.getProductsMgr().getProduct(addedItem.productId) as ProductModel;
						const productCategory = categoryActions.getCategory(product.getCategoryId());
						const productCategoryTree = productCategory?.getParentCategoryTree();
						let dataLayerObj: CartEventDataLayer = {
							eventSourceCategoryId: productCategory?.getId(),
							eventSourceCategoryName: productCategory?.getName(),
							productItem: {...basketItem, quantity: addedItem.quantity},
							productData: {
								product: product,
								variationGroupIdForCartEvents: DATALAYER_CONFIGS.variationGroupIdForCartEvents,
								productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
							},
						};

						if (dataLayer?.source) {
							dataLayerObj = {...dataLayerObj, source: dataLayer.source};
						}

						if (dataLayer?.sourceSpecification) {
							dataLayerObj = {
								...dataLayerObj,
								productData: {
									...dataLayerObj.productData,
									eventSourceCategoryId: productCategory?.getId(),
									eventSourceCategoryName: productCategory?.getName(),
								},
							};
						}

						if (DATALAYER_CONFIGS.addDimensions) {
							dataLayerObj.productData.attributes = {
								...dataLayerObj.productData.attributes,
								dimension9: product.getSeason(),
								dimension10: product.getPriceModel().isSalesPriceBookApplied() ? 'MD' : 'FP',
							};
						}

						if (DATALAYER_CONFIGS.addIsPreOrder) {
							dataLayerObj.productData.attributes = {
								...dataLayerObj.productData.attributes,
								isPreOrder: product.isPreOrderShown() ? 1 : 0,
							};
						}

						managerRepository.getEventsMgr().emit(EEventType.PRODUCT_ADD_TO_CART, dataLayerObj);
					}
				});
			},

			/**
			 * Remove an item from the basket.
			 *
			 * @param {string} itemId - The id of the basket item.
			 */
			async removeItemFromBasket(
				itemId: string,
				dataLayer: {source?: string; sourceSpecification?: string} = {},
			) {
				const productItem = basket.productItems.find((item) => item.itemId === itemId);

				const response = await api.shopperBaskets.removeItemFromBasket({
					parameters: {basketId: basket.basketId, itemId: itemId},
				});

				if (response.fault || response.isError) {
					throw new Error(response);
				} else {
					setBasket(response);

					const product = managerRepository.getProductsMgr().getProduct(productItem.productId) as ProductModel;
					const productCategory = categoryActions.getCategory(product.getCategoryId());
					const productCategoryTree = productCategory?.getParentCategoryTree();
					let dataLayerObj: CartEventDataLayer = {
						eventSourceCategoryId: productCategory?.getId(),
						eventSourceCategoryName: productCategory?.getName(),
						productData: {
							product: managerRepository.getProductsMgr().getProduct(productItem.productId),
							variationGroupIdForCartEvents: DATALAYER_CONFIGS.variationGroupIdForCartEvents,
							productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
							quantity: productItem?.quantity
						},
					};

					if (dataLayer?.source) {
						dataLayerObj = {...dataLayerObj, source: dataLayer.source};
					}

					if (dataLayer?.sourceSpecification) {
						dataLayerObj = {
							...dataLayerObj,
							productData: {
								...dataLayerObj.productData,
								eventSourceCategoryId: productCategory?.getId(),
								eventSourceCategoryName: productCategory?.getName(),
							},
						};
					}

					if (DATALAYER_CONFIGS.addDimensions) {
						dataLayerObj.productData.attributes = {
							dimension9: product.getSeason(),
							dimension10: product.getPriceModel().isSalesPriceBookApplied() ? 'MD' : 'FP',
						};
					}

					managerRepository.getEventsMgr().emit(EEventType.PRODUCT_REMOVE_FROM_CART, dataLayerObj);
				}
			},

			/**
			 * Update an item in the basket.
			 *
			 * @param {object} item
			 * @param {string} item.productId - The id of the product.
			 * @param {number} item.quantity - The quantity of the item.
			 * @param {string} basketItemId - The id of the item.
			 */
			async updateItemInBasket(item, basketItemId) {
				const productItem = basket.productItems.find((item) => item.itemId === basketItemId);

				const response = await api.shopperBaskets.updateItemInBasket({
					body: item,
					parameters: {basketId: basket.basketId, itemId: basketItemId},
				});

				if (response.fault || response.isError) {
					throw new Error(response);
				} else {
					setBasket(response);

					const product = managerRepository.getProductsMgr().getProduct(productItem.productId) as ProductModel;
					const productCategory = categoryActions.getCategory(product.getCategoryId());
					const productCategoryTree = productCategory?.getParentCategoryTree();
					let dataLayerObj: CartEventDataLayer = {
						eventSourceCategoryId: productCategory?.getId(),
						eventSourceCategoryName: productCategory?.getName(),
						productData: {
							product: managerRepository.getProductsMgr().getProduct(productItem.productId),
							variationGroupIdForCartEvents: DATALAYER_CONFIGS.variationGroupIdForCartEvents,
							productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
						},
					};

					if (DATALAYER_CONFIGS.addDimensions) {
						dataLayerObj.productData.attributes = {
							dimension9: product.getSeason(),
							dimension10: product.getPriceModel().isSalesPriceBookApplied() ? 'MD' : 'FP',
						};
					}

					if (DATALAYER_CONFIGS.addProductSku) {
						dataLayerObj.productItem = {
							productId: productItem.productId,
						};
					}

					managerRepository.getEventsMgr().emit(
						productItem.quantity > item.quantity ? EEventType.PRODUCT_REMOVE_FROM_CART : EEventType.PRODUCT_ADD_TO_CART,
						dataLayerObj
					);
				}
			},

			/**
			 * Adds a gift certificate item to the basket.
			 * @param {Object} item - incoming data to send with api request
			 */
			async addGiftCertificateItemToBasket(item) {
				const response = await api.shopperBaskets.addGiftCertificateItemToBasket({
					body: item,
					parameters: {
						basketId: basket.basketId,
					},
				});

				if (response.isError) {
					throw new Error(response);
				} else {
					setBasket(response);
				}
			},

			/**
			 * Updates a gift certificate item of an existing basket.
			 * @param {string} gid - gift certificate line item id
			 * @param {Object} item - incoming data to send with api request
			 */
			async updateGiftCertificateItemInBasket(gid, item) {
				const response = await api.shopperBaskets.updateGiftCertificateItemInBasket({
					body: item,
					parameters: {
						basketId: basket.basketId,
						giftCertificateItemId: gid,
					},
				});

				if (response.isError) {
					throw new Error(response);
				} else {
					setBasket(response);
				}
			},

			/**
			 * Removes gift certificate item from basket.
			 * @param {string} giftCertificateItemId - gift certificate item id
			 */
			async removeGiftCertificateItemFromBasket(giftCertificateItemId) {
				const response = await api.shopperBaskets.removeGiftCertificateItemFromBasket({
					parameters: {
						basketId: basket.basketId,
						giftCertificateItemId,
					},
				});

				if (response.isError) {
					throw new Error(response);
				} else {
					setBasket(response);
				}
			},

			/**
			 * Set the shipping address for the current basket.
			 * @external Address
			 * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
			 */
			async setShippingAddress(address) {
				const response = await api.shopperBaskets.updateShippingAddressForShipment({
					body: address,
					parameters: {
						basketId: basket.basketId,
						shipmentId: 'me',
					},
				});

				setBasket(response);
			},

			/**
			 * Set the shipping method for the current basket.
			 *
			 * @param {string} id - The id of the shipping method.
			 */
			async setShippingMethod(id) {
				const response = await api.shopperBaskets.updateShippingMethodForShipment({
					body: {id},
					parameters: {basketId: basket.basketId, shipmentId: 'me'},
				});

				setBasket(response);
			},

			/**
			 * Returns basket initialization id
			 * @returns {string} - basket initialization id
			 */
			getInitializationId(): string {
				return basket.initializationId;
			},

			/**
			 * Inits the SFCC Basket for hybrid deployment
			 * It is important to run this call at least one time before Context-Set for new customers
			 * otherwise the context on SFCC side is not correctly initialized
			 * @param {string} basketId - basket id
			 */
			async initSfccBasket(basketId: string) {
				return await sfccAPI.initBasket(basketId).then((res) => {
					setInitializationId(uuidv4());

					return res;
				});
			},

			/**
			 * Set the billing address for the current basket.
			 * @external Address
			 * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
			 */
			async setBillingAddress(address) {
				const response = await api.shopperBaskets.updateBillingAddressForBasket({
					body: address,
					parameters: {basketId: basket.basketId, shipmentId: 'me'},
				});

				setBasket(response);
			},

			/**
			 * Set the payment instrument for the current basket
			 *
			 * NOTE: API does allow adding multiple payment instruments to split payment. However,
			 * we are currently only handling a single payment instrument.
			 * Commerce API does not have an endpoint to edit a payment instrument, but OCAPI does.
			 * We want to emulate Commerce API behavior (when using OCAPI) so we'll just remove the
			 * existing payment and add the new one to simulate editing. We're making an assumption
			 * that our basket will never have more than one payment instrument applied at any time.
			 *
			 * @external PaymentInstrument
			 * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#basketpaymentinstrumentrequest
			 */
			async setPaymentInstrument(paymentInstrument) {
				// Keep reference to existing payment instrument id
				let existingPaymentInstrumentId =
					basket.paymentInstruments && basket.paymentInstruments[0]?.paymentInstrumentId;
				// Add the new payment instrument to basket
				let response = await api.shopperBaskets.addPaymentInstrumentToBasket({
					body: paymentInstrument,
					parameters: {basketId: basket.basketId},
				});

				// TOOO: Handle possible error when adding payment instrument.
				// We won't attempt to remove the previous payment instrument in this case.

				// Remove the previous payment instrument if it existed
				if (existingPaymentInstrumentId) {
					response = await api.shopperBaskets.removePaymentInstrumentFromBasket({
						parameters: {
							basketId: basket.basketId,
							paymentInstrumentId: existingPaymentInstrumentId,
						},
					});
				}

				setBasket(response);
			},

			/**
			 * Remove the payment instrument for the current basket
			 */
			async removePaymentInstrument() {
				let paymentInstrumentId =
					basket.paymentInstruments && basket.paymentInstruments[0]?.paymentInstrumentId;

				if (!paymentInstrumentId) {
					return;
				}

				const response = await api.shopperBaskets.removePaymentInstrumentFromBasket({
					parameters: {
						basketId: basket.basketId,
						paymentInstrumentId: paymentInstrumentId,
					},
				});

				setBasket(response);
			},

			/**
			 * Update the customer information for the current basket.
			 *
			 * @param {object} info
			 * @param {string} info.email - The email of the customer.
			 */
			async updateCustomerInfo(info) {
				const [response] = await Promise.all([
					api.shopperBaskets.updateCustomerForBasket({
						body: info,
						parameters: {basketId: basket.basketId},
					}),
				]);

				setBasket(response);
			},

			/**
			 * Apply a coupon/promo code to the current basket
			 * @param {string} code - The promo code to be applied
			 */
			async applyPromoCode(code) {
				const response = await api.shopperBaskets.addCouponToBasket({
					body: {code},
					parameters: {basketId: basket.basketId},
				});

				if (response.fault || response.isError) {
					throw new Error(response);
				}

				(managerRepository.getProductsMgr() as any).markProductsToBeRefreshed(true);

				setBasket(response);
			},

			/**
			 * Remove a coupon/promo code from the current basket
			 * @param {string} couponItemId - The item id of the appied code
			 */
			async removePromoCode(couponItemId) {
				const response = await api.shopperBaskets.removeCouponFromBasket({
					parameters: {basketId: basket.basketId, couponItemId},
				});

				if (response.fault || response.isError) {
					throw new Error(response);
				}

				(managerRepository.getProductsMgr() as any).markProductsToBeRefreshed(true);

				setBasket(response);
			},

			/**
			 * Fetches and returns promo details for the given IDs
			 * @param {Array<string>} ids - The promo ids to fetch
			 * @returns {Object} - API response containing data
			 */
			async getPromotions(ids) {
				const response = await api.shopperPromotions.getPromotions({
					parameters: {ids: ids.join(',')},
				});

				return response;
			},

			/**
			 * Creates an order using the current basket.
			 */
			async createOrder() {
				const response = await api.shopperOrders.createOrder({
					body: {basketId: basket.basketId},
				});

				if (response.fault || response.isError || (response.title && response.type && response.detail)) {
					throw new Error(response.title);
				}

				// We replace the basket with the order result data so we can display
				// it on the confirmation page. The basket is automatically deleted
				// in SF so we need to make sure a new one is created when leaving the confirmation.
				setBasket(response);
			},

			/**
			 * Fetches the applicable shipping methods for the current basket
			 * @returns {Object} - API response containing data
			 */
			async getShippingMethods() {
				const shippingMethods = await api.shopperBaskets.getShippingMethodsForShipment({
					parameters: {basketId: basket.basketId, shipmentId: 'me'},
				});

				const defaultShippingMethodId: string | undefined = shippingMethods?.defaultShippingMethodId;
				// TODO: Add TypeScript support
				const applicableShippingMethods: any[] = shippingMethods?.applicableShippingMethods;

				if (applicableShippingMethods?.length) {
					const firstApplicableShippingMethod = applicableShippingMethods[0];

					if (!defaultShippingMethodId) {
						shippingMethods.defaultShippingMethodId = firstApplicableShippingMethod.id;
					} else {
						const applicableDefaultShippingMethod = applicableShippingMethods.find(
							(shippingMethod) => shippingMethod.id.indexOf(defaultShippingMethodId) !== -1,
						);

						if (!defaultShippingMethodId || !applicableDefaultShippingMethod) {
							shippingMethods.defaultShippingMethodId = firstApplicableShippingMethod.id;
						} else if (applicableDefaultShippingMethod.id !== defaultShippingMethodId) {
							shippingMethods.defaultShippingMethodId = applicableDefaultShippingMethod.id;
						}
					}
				}

				return shippingMethods;
			},

			/**
			 * Merge data from the previous shopper's basket into the current shopper's active basket
			 * and delete the previous shopper's basket.
			 */
			async mergeBasket() {
				const response = await api.shopperBaskets.mergeBasket({
					headers: {
						'Content-Type': 'application/json', // This is not required since the request has no body but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
					},
					parameters: {
						createDestinationBasket: true, // If the current shopper has an active basket, this parameter is ignored.
					},
				});

				if (response.fault || response.isError) {
					throw new Error(response);
				}

				setBasket(response);
			},

			async deleteCurrentBasket() {
				const response = await api.shopperBaskets.deleteBasket({
					parameters: {
						basketId: basket.basketId,
					},
				});

				if (response && (response.fault || response.isError)) {
					throw new Error(response);
				}

				setBasket(null);
			},

			async moveBasketItemToWishlist(item: ProductItemModel) {
				await wishlist
					.createListItem({
						id: item.getProductId(),
						c_variationGroupId: item.getVariationGroupId(),
						quantity: 1,
					})
					.then((response) => {
						if (response.fault || response.isError) {
							throw new Error(response);
						}

						return this.removeItemFromBasket(item.getItemId());
					});
			},

			/**
			 * Creates a new wishlist items and then search and remove the corresponding item in basket
			 * Since we get item from redux, we stored only serializable data we need
			 * @param {IMoveProductFromBasketToWishlist} item
			 * @param {string} item.productId - Variant id of product to remove
			 * @param {string} item.c_variationGroupId - Variation id of product to remove
			 */
			async moveBasketItemToWishlistPartialItem(
				item: IMoveProductFromBasketToWishlist,
				dataLayerObj,
			): Promise<void> {
				const {productId, c_variationGroupId} = item as any;
				const itemId = basket.productItems.find((basketItem) => basketItem.productId === productId)?.itemId;

				const response = await wishlist.createListItem(
					{
						id: productId,
						c_variationGroupId: c_variationGroupId,
						quantity: 1,
					},
					dataLayerObj,
				);

				if (response.fault || response.isError) {
					throw new Error(response);
				}

				if (itemId) {
					await this.removeItemFromBasket(itemId, dataLayerObj);
				}
			},

			async moveWishlistItemToBasket(item, dataLayer = {}) {
				await this.addItemToBasket(
					[
						{
							productId: item.productId,
							quantity: item.quantity,
							c_variationGroupId: item.c_variationGroupId || null,
							c_customProducts: item.c_customProducts || null,
						},
					],
					dataLayer,
				).then(() => {
					wishlist.removeListItem(item.id);
				});
			},

			async fetchPaymentMethodsForBasket() {
				// TODO: update locale handling
				let paymentMethodObjects = [];
				const response = await api.shopperBaskets.getPaymentMethodsForBasket({
					parameters: {
						basketId: basket.basketId
					},
				});

				if (response.isError) {
					throw new Error('Error');
				} else {
					const applicablePaymentMethods = response?.applicablePaymentMethods || [];

					paymentMethodObjects = applicablePaymentMethods.map((paymentMethodObject) => {
						// transform expressButtons JSON into object
						try {
							paymentMethodObject.c_expressButtons = paymentMethodObject.c_expressButtons
								? JSON.parse(paymentMethodObject.c_expressButtons)
								: {};
							paymentMethodObject.c_excludedProducts = paymentMethodObject.c_excludedProducts
								? paymentMethodObject.c_excludedProducts.split(',')
								: [];
						} catch (e) {
							throw new Error('Unable to parse custom fields for applicablePaymentMethods');
						}

						return paymentMethodObject;
					});

					setApplicablePaymentMethods(paymentMethodObjects);
				}
			},

			async upsertAbandonedCart() {
				try {
					const response = await api.customShopperAbandonedCart.upsertAbandonedCart({});

					if (response.fault || response.isError) {
						throw new Error(response.message);
					}

					return response;
				} catch (error) {
					console.error('Error creating abandoned cart:', error);

					throw error;
				}
			},

			async getApproachingDiscounts() {
				if (!basket?.basketId) {
					return null;
				}

				const response = await api.shopperBaskets.getApproachingDiscounts({
					parameters: {basketId: basket.basketId},
				});

				if (isError(response)) {
					throw new Error(response.detail);
				}

				return response;
			},
		};
	}, [customer, basket, setBasket]);

	return self;
}
